import "zod/compile";
import { z } from "zod";
import {
  changeSetCommentCellKey,
  changeSetCommentRowKey,
  type ChangeSetCommentRow,
} from "./change-set-comments.contract";
import {
  As400ExportGroupSchema,
  As400ExportRowSourceSchema,
} from "./products.contract";

// ─── Review mode ─────────────────────────────────────────────────────────────
// A review stripped down to what it changes: one line per cell, Old (the live
// AS/400 ingest value) → New (the staged value), over the whole change set —
// the grid's per-review filters don't apply. Lines are grouped by ITEMNO + CDB
// and paged by group, so a page never splits one.

/**
 * - `changed`: a staged edit that still differs from live (the CHANGE upload's
 *   rule; a cell whose live value has since moved to the staged one is not a
 *   change and is not listed).
 * - `added`: a populated cell of an authored new row; Old is blank.
 * - `no_change`: an unchanged cell listed because a comment thread sits on it;
 *   Old = New = the live value.
 */
export const ChangeSetReviewLineKindSchema = z.enum([
  "changed",
  "added",
  "no_change",
]);
export type ChangeSetReviewLineKind = z.infer<
  typeof ChangeSetReviewLineKindSchema
>;

export const ChangeSetReviewLineSchema = z.object({
  // The record's item number (the worksheet's item column, live) and its
  // company/division/branch key — the same CDB the grid's leading column shows.
  itemNumber: z.string().nullable(),
  cdb: z.string().nullable(),
  worksheet: As400ExportGroupSchema,
  // The division (DataSource) whose upload file carries the record.
  dataSourceId: z.uuid(),
  // The cell's edit key (an ingest field code, or an export-only column key)
  // and its worksheet header.
  column: z.object({ code: z.string(), header: z.string() }),
  oldValue: z.string().nullable(),
  newValue: z.string().nullable(),
  kind: ChangeSetReviewLineKindSchema,
  // Where the cell lives: the ingest row it is staged on, or the authored new
  // row. Exactly one is set.
  source: As400ExportRowSourceSchema.nullable(),
  newRowId: z.uuid().nullable(),
  // Comment threads on this cell, resolved ones included (matched as
  // `gridCellCommentKeys` matches them, so a joined row's comment on either
  // source counts). Orphaned threads are never listed.
  threadIds: z.array(z.uuid()),
});
export type ChangeSetReviewLine = z.infer<typeof ChangeSetReviewLineSchema>;

export const ChangeSetReviewLineGroupSchema = z.object({
  // `itemNumber` + `cdb`, stable across pages.
  key: z.string(),
  itemNumber: z.string().nullable(),
  cdb: z.string().nullable(),
  // How many lines the group holds, so the UI can start a long group
  // collapsed.
  lineCount: z.number().int().positive(),
  lines: z.array(ChangeSetReviewLineSchema),
});
export type ChangeSetReviewLineGroup = z.infer<
  typeof ChangeSetReviewLineGroupSchema
>;

/** The row a comment on the line's cell anchors to: its ingest row or new row. */
export function reviewLineCommentRow(
  line: Pick<ChangeSetReviewLine, "source" | "newRowId">,
): ChangeSetCommentRow | null {
  if (line.newRowId) return { kind: "new", stagedNewRowId: line.newRowId };
  if (line.source) return { kind: "data", ...line.source };
  return null;
}

/** The line's cell, as a comment's `anchor.cellKey` names it. */
export function reviewLineCellKey(
  line: Pick<
    ChangeSetReviewLine,
    "worksheet" | "column" | "source" | "newRowId"
  >,
): string | null {
  const row = reviewLineCommentRow(line);
  return row
    ? changeSetCommentCellKey({
        group: line.worksheet,
        rowKey: changeSetCommentRowKey(row),
        columnKey: line.column.code,
      })
    : null;
}

/**
 * Where the line showing the cell `cellKey` is among `groups`: the group's
 * index and the line. A line shows a cell when it is that cell, or when a
 * thread on that cell sits on it (`threads` are the review's) — a joined
 * Purchase Data row lists a cell once, under one of its two sources.
 */
export function findReviewLineOfCell(
  groups: readonly Pick<ChangeSetReviewLineGroup, "lines">[],
  cellKey: string,
  threads: readonly { id: string; anchor: { cellKey: string } }[],
): { groupIndex: number; line: ChangeSetReviewLine } | null {
  const cellThreadIds = new Set(
    threads
      .filter((thread) => thread.anchor.cellKey === cellKey)
      .map((thread) => thread.id),
  );
  for (const [groupIndex, group] of groups.entries()) {
    const line = group.lines.find(
      (candidate) =>
        reviewLineCellKey(candidate) === cellKey ||
        candidate.threadIds.some((threadId) => cellThreadIds.has(threadId)),
    );
    if (line) return { groupIndex, line };
  }
  return null;
}

// Review mode's own filters, separate from the grid's.
export const ChangeSetReviewLineFiltersSchema = z.object({
  worksheet: As400ExportGroupSchema.optional(),
  // A column's edit key; combined with `worksheet` when both are set.
  column: z.string().min(1).optional(),
});
export type ChangeSetReviewLineFilters = z.infer<
  typeof ChangeSetReviewLineFiltersSchema
>;

export const ListChangeSetReviewLinesInputSchema =
  ChangeSetReviewLineFiltersSchema.extend({
    changeSetId: z.uuid(),
    page: z.number().int().min(1).default(1),
    // Groups per page, not lines.
    pageSize: z.number().int().min(1).max(200).default(50),
    // Return the page holding this cell's line (a `changeSetCommentCellKey`),
    // in place of `page` — jumping to a comment's cell. `page` stands when no
    // listed line shows the cell; `pageInfo.currentPage` says which was sent.
    focusCellKey: z.string().min(1).max(500).optional(),
  });
export type ListChangeSetReviewLinesInput = z.infer<
  typeof ListChangeSetReviewLinesInputSchema
>;

// The Review-mode list as an Excel download: the whole list, unpaged, under
// the same filters.
export const ExportChangeSetReviewLinesInputSchema =
  ChangeSetReviewLineFiltersSchema.extend({ changeSetId: z.uuid() });
export type ExportChangeSetReviewLinesInput = z.infer<
  typeof ExportChangeSetReviewLinesInputSchema
>;

export const ListChangeSetReviewLinesResponseSchema = z.object({
  groups: z.array(ChangeSetReviewLineGroupSchema),
  pageInfo: z.object({
    currentPage: z.number().int(),
    totalPages: z.number().int(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
    totalGroups: z.number().int(),
    totalLines: z.number().int(),
  }),
});
export type ListChangeSetReviewLinesResponse = z.infer<
  typeof ListChangeSetReviewLinesResponseSchema
>;
